import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/core/utils/cn';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  contentClassName?: string;

  // New props for customization
  titleClassName?: string;
  bodyClassName?: string;
  closeButtonClassName?: string;
  overlayClassName?: string;
  contentWrapperClassName?: string;
}
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  contentClassName,
  showCloseButton = true,
  size = 'md',
  titleClassName,
  bodyClassName,
  closeButtonClassName,
  overlayClassName,
  contentWrapperClassName,
}: ModalProps) {
  const { t } = useTranslation(['common']);

  // Check if modal is simple (sm, md) or complex (lg, xl, 2xl, full)
  const isSimpleModal = size === 'sm' || size === 'md';

  // Responsive size classes
  // Simple modals (sm, md): Centered on all devices, fit content
  // Complex modals (lg, xl, 2xl, full): Full-screen on small mobile, centered on tablet/desktop
  const sizeClasses = {
    sm: 'w-[95%] max-w-sm', // Simple: centered, fit content on all devices
    md: 'w-[95%] max-w-md', // Simple: centered, fit content on all devices
    lg: 'w-full max-sm:h-full sm:w-[95%] md:max-w-lg', // Complex: full-screen small mobile, centered tablet/desktop
    xl: 'w-full max-sm:h-full sm:w-[95%] md:max-w-xl', // Complex: full-screen small mobile, centered tablet/desktop
    '2xl': 'w-full max-sm:h-full sm:w-[95%] md:max-w-2xl', // Complex: full-screen small mobile, centered tablet/desktop
    full: 'w-full max-sm:h-full sm:w-[95%] md:w-[90vw] md:max-w-5xl', // Complex: full-screen small mobile, centered tablet/desktop
  };

  // Positioning classes - different for simple vs complex modals
  const positionClasses = isSimpleModal
    ? 'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]' // Simple: always centered
    : 'max-sm:inset-0 sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]'; // Complex: full-screen small mobile, centered tablet/desktop
  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        {/* Overlay */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[998] bg-black/40 duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            overlayClassName
          )}
        />

        {/* Modal Content */}
        <DialogPrimitive.Content
          onInteractOutside={(event) => event.preventDefault()}
          className={cn(
            // Base styles
            'fixed z-[999] bg-white shadow-lg duration-300 ease-in-out',
            // Positioning - conditional based on modal size
            positionClasses,
            // Border and rounded corners
            isSimpleModal ? 'rounded-xl border' : 'sm:rounded-xl sm:border',
            // Animations
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            // Zoom and slide animations - conditional based on modal size
            isSimpleModal
              ? 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
              : 'sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]',
            // Size classes (responsive)
            sizeClasses[size],
            className
          )}
        >
          {/* Wrapper padding - responsive */}
          <div
            className={cn(
              // Padding: p-6 for simple, p-4 mobile / p-6 desktop for complex
              isSimpleModal ? 'p-6' : 'p-4 lg:p-6',
              // Overflow: allow scrolling when content is tall
              'overflow-y-auto',
              // Vertical centering only on small mobile for complex modals
              !isSimpleModal &&
                'max-sm:flex max-sm:min-h-full max-sm:flex-col max-sm:justify-center',
              // Custom classes
              contentClassName,
              contentWrapperClassName
            )}
          >
            {/* Title - Responsive sizing */}
            {title && (
              <DialogPrimitive.Title
                className={cn(
                  'mb-3 text-xl font-semibold text-neutralUltraDark lg:mb-4 lg:text-2xl',
                  titleClassName
                )}
              >
                {title}
              </DialogPrimitive.Title>
            )}

            {/* Close Button - Touch-friendly on mobile */}
            {showCloseButton && (
              <DialogPrimitive.Close
                data-name="modal-close-button"
                className={cn(
                  'absolute right-3 top-3 lg:right-4 lg:top-4',
                  'min-h-[44px] min-w-[44px] lg:min-h-0 lg:min-w-0', // Touch-friendly on mobile/tablet
                  'flex items-center justify-center', // Center icon in touch target
                  'rounded-sm opacity-70 transition-opacity transition-transform duration-300 ease-in-out',
                  'hover:scale-105 hover:opacity-100 focus-visible:outline-none disabled:pointer-events-none',
                  closeButtonClassName
                )}
              >
                <X className="h-6 w-6 text-neutralMedium transition-transform duration-300 ease-in-out hover:scale-125 hover:text-neutralDark lg:h-5 lg:w-5" />
                <span className="sr-only">{t('close')}</span>
              </DialogPrimitive.Close>
            )}

            {/* Modal Body */}
            <div className={cn(bodyClassName)}>{children}</div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
